import { Stack } from '@mui/material';
import { useCallback, memo, useMemo, useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { HikStorageParams, Templates } from '@thive/fddm-types';

import { Accordion } from '@common/components/Accordion';
import { PropertiesLabel } from '@/modules/common/components/PropertiesLabel';
import { WorkspaceMode } from '@/modules/common/types/general';
import {
  getMultiSelectDisplayValue,
  MultiSelectInput,
  NumberInput,
} from '@/modules/common/components/inputs';
import { primitiveArraysHaveEqualContents } from '@/modules/common/helpers/array';
import { useAppStore } from '@/modules/app/store/useAppStore';
import { LoadingServiceFactory } from '@/modules/app/service/LoadingServiceFactory';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';

import { TpsServiceFactory } from '../service/TpsServiceFactory';
import { DisabledReasonTooltip } from '@/modules/salesWorkspace/components/propertyPanel/layout/components/common/DisabledReasonTooltip';
import { UserPreferenceName } from '@/modules/userPreferences';
import { getRegeneratedTemplateId } from '../helpers';
import {
  FIELDS_PADDING_BOTTOM,
  FIELDS_SPACING,
  PANEL_ACCORDION_SX,
} from '@/modules/common/constants/propertyPanel';

interface Props {
  selectedHikStorages: NonNullable<Templates['hikStorages']>;
}

function HikTemplateParametersComponent({ selectedHikStorages }: Props) {
  const { t } = useTranslation();
  const mode = useAppStore((state) => state.mode);
  const loadingSvc = useMemo(() => LoadingServiceFactory.getInstance().createLoadingService(), []);
  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);
  const tpsSvc = useMemo(() => TpsServiceFactory.getInstance().createTpsService(), []);

  const hikGroup = selectedHikStorages[0];
  const newGenerationParams = hikGroup?.params;

  const [crossAislesAfterBays, setCrossAislesAfterBays] = useState<number[]>([]);

  useEffect(() => {
    // PLZ FIX ME
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setCrossAislesAfterBays(newGenerationParams?.crossAislesAfterBays || []);
  }, [newGenerationParams?.crossAislesAfterBays]);

  const crossAislesOptions = useMemo(() => {
    if (!newGenerationParams) return [];

    return Array.from(Array(newGenerationParams.numBays - 1), (el, i) => i + 1).map((val) => ({
      label: t('interface:properties.template.crossAisles.option_label', {
        value: val,
      }),
      value: String(val),
    }));
  }, [newGenerationParams, t]);

  const crossAislesDisplayValue = useMemo(() => {
    if (!newGenerationParams) return '';

    return getMultiSelectDisplayValue(
      crossAislesAfterBays.map((val) => ({
        name: String(val),
        id: String(val),
      })),
      crossAislesAfterBays.map((item) => String(item)),
    );
  }, [crossAislesAfterBays, newGenerationParams]);

  const regenerate = useCallback(
    async (params?: Partial<HikStorageParams>) => {
      if (!newGenerationParams || !hikGroup) return;

      loadingSvc.showLoadContainer('TRANSPARENT');

      const regeneration = await tpsSvc.regenerateHikTemplate({
        ...hikGroup,
        params: { ...newGenerationParams, ...params },
      });
      // Regenerating can give the template a new id: keep it selected
      tpsSvc.selectTemplates([getRegeneratedTemplateId(regeneration) ?? hikGroup.group.id]);

      loadingSvc.hideLoadContainer();
    },
    [newGenerationParams, hikGroup, tpsSvc, loadingSvc],
  );

  const onNumAislesChange = useCallback(
    (newValue: number) => {
      fddmSvc.setPreference('template.hik.numAisles', newValue);
      regenerate({ numAisles: newValue });
    },
    [fddmSvc, regenerate],
  );

  const onNumBaysChange = useCallback(
    (newValue: number) => {
      fddmSvc.setPreference('template.hik.numBays', newValue);

      // remove cross aisles that exceed the new number of bays
      const newCrossAislesAfterBays = crossAislesAfterBays.filter(
        (crossAisle) => crossAisle < newValue,
      );

      regenerate({ numBays: newValue, crossAislesAfterBays: newCrossAislesAfterBays });
    },
    [crossAislesAfterBays, fddmSvc, regenerate],
  );

  const onNumShelvesChange = useCallback(
    (newValue: number) => {
      fddmSvc.setPreference('template.hik.numShelves', newValue);
      regenerate({ numShelves: newValue });
    },
    [fddmSvc, regenerate],
  );

  const onCrossAisleOptionClick = useCallback(
    (value: string) => {
      if (!newGenerationParams) return;

      const newValue = Number(value);
      const { numBays } = newGenerationParams;
      let updated = [...crossAislesAfterBays];
      updated = updated.filter((crossAisle) => crossAisle <= numBays);
      updated = updated.includes(newValue)
        ? updated.filter((item) => item !== newValue)
        : [...updated, newValue];
      updated.sort((a, b) => a - b);

      setCrossAislesAfterBays(updated);
    },
    [crossAislesAfterBays, newGenerationParams],
  );

  const onCrossAisleCommit = useCallback(() => {
    if (!newGenerationParams) return;
    if (
      primitiveArraysHaveEqualContents(
        newGenerationParams.crossAislesAfterBays,
        crossAislesAfterBays,
      )
    )
      return;

    regenerate({ crossAislesAfterBays });
  }, [crossAislesAfterBays, newGenerationParams, regenerate]);

  // Makes sure the onCrossAisleCommit is called when the component unmounts
  const onCrossAisleCommitRef = useRef(onCrossAisleCommit);
  useEffect(() => {
    onCrossAisleCommitRef.current = onCrossAisleCommit;
  }, [onCrossAisleCommit]);
  useEffect(
    () => () => {
      onCrossAisleCommitRef.current();
    },
    [],
  );

  const onlyOneSelected = selectedHikStorages.length === 1;
  const isDisabled = mode !== WorkspaceMode.EDITABLE || !onlyOneSelected;

  if (!newGenerationParams) return null;

  return (
    <Accordion
      persistId={UserPreferenceName.ACCORDION_PROPERTY_PANEL_TEMPLATE_HIK}
      title={t('interface:properties.template_title.hik', 'Hik')}
      unmountOnExit
      sx={PANEL_ACCORDION_SX}
    >
      <Stack direction='column' spacing={FIELDS_SPACING} pb={FIELDS_PADDING_BOTTOM}>
        <PropertiesLabel i18nkey='interface:properties.template.aisles'>
          <DisabledReasonTooltip
            reasonKey={
              onlyOneSelected ? undefined : 'interface:properties.area.templates.partial_selection'
            }
          >
            <NumberInput
              value={newGenerationParams.numAisles}
              onChange={onNumAislesChange}
              minValue={1}
              disabled={isDisabled}
            />
          </DisabledReasonTooltip>
        </PropertiesLabel>
        <PropertiesLabel i18nkey='interface:properties.template.bays'>
          <DisabledReasonTooltip
            reasonKey={
              onlyOneSelected ? undefined : 'interface:properties.area.templates.partial_selection'
            }
          >
            <NumberInput
              value={newGenerationParams.numBays}
              onChange={onNumBaysChange}
              disabled={isDisabled}
              minValue={1}
            />
          </DisabledReasonTooltip>
        </PropertiesLabel>
        <PropertiesLabel i18nkey='interface:properties.template.shelves'>
          <DisabledReasonTooltip
            reasonKey={
              onlyOneSelected ? undefined : 'interface:properties.area.templates.partial_selection'
            }
          >
            <NumberInput
              value={newGenerationParams.numShelves}
              onChange={onNumShelvesChange}
              minValue={1}
              disabled={isDisabled}
            />
          </DisabledReasonTooltip>
        </PropertiesLabel>
        <PropertiesLabel i18nkey='interface:properties.template.crossAisles.input_label'>
          <DisabledReasonTooltip
            reasonKey={
              onlyOneSelected ? undefined : 'interface:properties.area.templates.partial_selection'
            }
          >
            <MultiSelectInput
              options={crossAislesOptions}
              selectedOptions={crossAislesAfterBays.map((item) => String(item))}
              onItemClick={onCrossAisleOptionClick}
              onClose={onCrossAisleCommit}
              displayValue={crossAislesDisplayValue}
              disabled={isDisabled}
            />
          </DisabledReasonTooltip>
        </PropertiesLabel>
      </Stack>
    </Accordion>
  );
}

export const HikTemplateParameters = memo(HikTemplateParametersComponent);
